import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { ArrowLeft, Bookmark, Loader2 } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { backgroundOptions } from "../../../utils/postConstants";
import { useCollectionPosts } from "../../../hooks/postQueries";
import FetchErrorState from "../../Common/FetchErrorState";
import { useNavigate } from "react-router-dom";

const getBackgroundClass = (backgroundKey) => {
  const bgOption = backgroundOptions[backgroundKey];
  return bgOption ? bgOption.class : backgroundOptions.none.class;
};

// Intersection Observer Hook for Lazy Loading
const useIntersectionObserver = (callback, options = {}) => {
  const targetRef = useRef(null);
  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          callback();
        }
      },
      { threshold: 0.1, rootMargin: "100px", ...options },
    );
    observer.observe(target);
    return () => {
      if (target) observer.unobserve(target);
    };
  }, [callback, options]);
  return targetRef;
};

const GridPostItem = React.memo(({ post, collectionType }) => {
  const navigate = useNavigate();
  // ✅ CHANGE 1: Use collectionId instead of id
  const handleClick = () =>
    navigate(`/collection/${collectionType}/p/${post.collectionId}`);
  const textColor = post.textColor === "white" ? "text-white" : "text-gray-900";
  return (
    <div
      className={`aspect-square rounded-md relative overflow-hidden ${post.backgroundStyle} cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg`}
      onClick={handleClick}
    >
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <h3
          className={`text-center font-bold text-lg md:text-xl ${textColor} ${post.fontStyle} line-clamp-3`}
        >
          {post.title}
        </h3>
      </div>
    </div>
  );
});

const CustomCollection = ({
  onBack,
  collectionType = "save",
  title = "Saved Posts",
  description = "Manage your bookmarked posts and content",
  emptyIcon: EmptyIcon = Bookmark,
  emptyTitle = "No Saved Posts",
  emptyDescription = "Start bookmarking posts to see them here",
}) => {
  // ✅ Pass null as initialCursor for grid view (start from beginning)
  const {
    data: collectionData,
    isLoading,
    error: queryError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useCollectionPosts(collectionType, 12, null);

  // ✅ Transform and flatten posts
  const allPosts = useMemo(() => {
    if (!collectionData) return [];
    return collectionData.pages
      .flatMap((page) => page.posts || [])
      .map((post) => ({
        id: post._id,
        _id: post._id,
        collectionId: post.collectionId, // ✅ CHANGE 2: Store collectionId from backend
        title: post.title || "",
        backgroundStyle: getBackgroundClass(post.styling?.background || "none"),
        fontStyle: post.styling?.font || "font-georgia",
        textColor: post.styling?.color || "black",
      }));
  }, [collectionData]);

  // ✅ Handle load more
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isLoading) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isLoading, fetchNextPage]);

  const loadMoreRef = useIntersectionObserver(handleLoadMore);

  // ✅ Loading state
  if (isLoading && allPosts.length === 0) {
    return (
      <div className="space-y-6 px-4 sm:px-0">
        <div className="bg-white py-2">
          <div className="flex items-center space-x-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              <p className="text-gray-600 text-sm">{description}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (queryError) {
    return (
      <div className="space-y-6 px-4 sm:px-0">
        <div className="bg-white py-2">
          <div className="flex items-center space-x-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              <p className="text-gray-600 text-sm">{description}</p>
            </div>
          </div>
        </div>
        <FetchErrorState
          message="We couldn't load this collection."
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  // ✅ Empty state
  if (allPosts.length === 0) {
    return (
      <div className="space-y-6 px-4 sm:px-0">
        <div className="bg-white py-2">
          <div className="flex items-center space-x-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              <p className="text-gray-600 text-sm">{description}</p>
            </div>
          </div>
        </div>
        <div className="text-center py-12">
          <EmptyIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {emptyTitle}
          </h3>
          <p className="text-gray-500">{emptyDescription}</p>
        </div>
      </div>
    );
  }

  // ✅ Render posts
  return (
    <div className="space-y-3 px-4 sm:px-0">
      <div className="bg-white py-2">
        <div className="flex items-center space-x-1">
          <button onClick={onBack} className="p-2">
            <ArrowLeft className="w-5 h-5 text-black" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          </div>
        </div>
      </div>

      <div className="w-full">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {allPosts.map((post) => (
            <GridPostItem
              key={post.id}
              post={post}
              collectionType={collectionType}
            />
          ))}
        </div>
      </div>

      {/* ✅ Infinite scroll trigger */}
      {hasNextPage && (
        <div ref={loadMoreRef} className="w-full px-4 sm:px-0">
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">
              Loading more items...
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomCollection;
